/**
 * TeamSystem Impact - MacroCategory API Client
 * API calls for IRO macro-categories, scoped per anno
 */

import { apiClient } from '../../../api/client';

export interface MacroCategory {
  _id: string;
  code: string;
  name: string;
  nameEN: string;
  order: number;
  isDefault: boolean;
  image?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

const BASE_PATH = '/dma/macro-categories';

export const macroCategoryApi = {
  list: async (year: number): Promise<MacroCategory[]> => {
    const response = await apiClient.get<ApiResponse<MacroCategory[]>>(
      `${BASE_PATH}?year=${year}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list macro-categories');
    return response.data.data!;
  },

  create: async (
    year: number,
    data: { code: string; name: string; nameEN: string }
  ): Promise<MacroCategory> => {
    const response = await apiClient.post<ApiResponse<MacroCategory>>(BASE_PATH, {
      assessmentYear: year,
      ...data,
    });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create macro-category');
    return response.data.data!;
  },

  update: async (
    id: string,
    data: { name?: string; nameEN?: string; order?: number; image?: string | null }
  ): Promise<MacroCategory> => {
    const response = await apiClient.put<ApiResponse<MacroCategory>>(
      `${BASE_PATH}/${id}`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update macro-category');
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(`${BASE_PATH}/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete macro-category');
  },

  seed: async (year: number): Promise<{ created: number; skipped: number }> => {
    const response = await apiClient.post<ApiResponse<{ created: number; skipped: number }>>(
      `${BASE_PATH}/seed`,
      { assessmentYear: year }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to seed macro-categories');
    return response.data.data!;
  },
};
